import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { type CreateSpaceDto, type Document as ImageDocument, SpacesService, type SpaceTab, type UpdateSpaceDto } from '@api/locations';
import { CURRENT_CLIENT_TEMPLATE_VAR, type DeepPartial, EntityStatus } from '@core';
import { map, Observable } from 'rxjs';

import { type LocationBaseModel, type LocationData, type Space } from '../models';

@Injectable({ providedIn: 'root' })
export class LocationService {
  private http = inject(HttpClient);
  private spacesApi = inject(SpacesService);

  getLocationsList(includeArchived = true): Observable<LocationData[]> {
    const locationStatus = [EntityStatus.Active, EntityStatus.Paused];

    if (includeArchived) {
      locationStatus.push(EntityStatus.Archived);
    }
    const queryString = locationStatus.map(status => `status=${status}`).join('&');

    return this.http
      .get<{ data: LocationData[]; }>(`locations/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/locations?${queryString}`)
      .pipe(map(({ data }) => data));
  }

  addLocation(clientId: string, data: LocationBaseModel) {
    return this.http.post<{ data: LocationData; }>(`locations/v1/clients/${clientId}/locations`, data).pipe(map(({ data }) => data));
  }

  updateLocation(
    clientId: string,
    locationId: string,
    { id, name, clientFriendlyId, ...data }: DeepPartial<LocationData>,
  ) {
    return this.http
      .patch<{ data: LocationData; }>(`locations/v1/clients/${clientId}/locations/${locationId}`, data)
      .pipe(map(({ data }) => data));
  }

  getLocationInfoById(locationId: string): Observable<LocationData> {
    return this.http
      .get<{
        data: LocationData;
      }>(`locations/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/locations/${locationId}`, {
        params: {
          deviceStatus: ['active', 'paused'],
        },
      })
      .pipe(map(({ data }) => data));
  }

  getSpacesList(locationId: string, params: Record<string, string> = {}): Observable<Space[]> {
    return this.http
      .get<{
        data: Space[];
      }>(`locations/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/locations/${locationId}/spaces`, { params })
      .pipe(map(({ data }) => data));
  }

  getSpaceInfoById(locationId: string, spaceId: string): Observable<Space> {
    return this.http
      .get<{
        data: Space;
      }>(`locations/v1/clients/${CURRENT_CLIENT_TEMPLATE_VAR}/locations/${locationId}/spaces/${spaceId}`, {
        params: {
          deviceStatus: ['active', 'paused'],
        },
      })
      .pipe(map(({ data }) => data));
  }

  addNewSpace<T = Space>(clientId: string, locationId: string, data: CreateSpaceDto) {
    return this.spacesApi
      .create<{ data: T; }>({ clientId, locationId }, data)
      .pipe(map(({ data }) => data));
  }

  updateSpace<T = Space>(clientId: string, locationId: string, spaceId: string, data: UpdateSpaceDto) {
    return this.spacesApi
      .update<{ data: T; }>({ clientId, locationId, spaceId }, data)
      .pipe(map(({ data }) => data));
  }

  uploadDocumentToSpace({ clientId, locationId, spaceId, body, tab }: {
    body: File;
    clientId: string;
    locationId: string;
    spaceId: string;
    tab: SpaceTab,
  }) {
    const params: { [key: string]: string | boolean; } = {};

    params['tab'] = tab;

    return this.http
      .post<{ data: ImageDocument; }>(`locations/v1/clients/${clientId}/locations/${locationId}/spaces/${spaceId}/documents`, body, {
        headers: new HttpHeaders({
          'Content-Type': 'image/svg+xml',
          FileName: body.name,
        }),
        params,
      })
      .pipe(map(({ data }) => data));
  }
}
